// useTenants — fetches /tenants with the current filters, exposes refetch().
//
// V1 keeps it deliberately simple: re-fetch on filter change, no SWR-style
// cache, no optimistic updates. Inline refetch() after any mutation
// (create, patch, suspend, …) is fine for the V1 tenant count (a handful
// for first design partners; per-page pagination kicks in at Phase 1 scale).
//
// Mirrors useUsers exactly.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ListTenantsQuery, ListTenantsResponse } from '@gc-coach/shared';
import { listTenants } from '../api/endpoints.js';

export interface UseTenantsResult {
  data: ListTenantsResponse | null;
  isLoading: boolean;
  errorCode: string | null;
  refetch: () => Promise<void>;
}

export function useTenants(query: ListTenantsQuery): UseTenantsResult {
  const [data, setData] = useState<ListTenantsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const key = useMemo(
    () =>
      `${query.status ?? ''}|${query.standing ?? ''}|${query.jurisdiction ?? ''}|${query.q?.trim() ?? ''}`,
    [query.status, query.standing, query.jurisdiction, query.q],
  );

  const fetchNow = useCallback(async () => {
    setIsLoading(true);
    const result = await listTenants(query);
    if (result.ok) {
      setData(result.data);
      setErrorCode(null);
    } else {
      setErrorCode(result.code);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    void fetchNow();
  }, [fetchNow]);

  return { data, isLoading, errorCode, refetch: fetchNow };
}
